import { observable, runInAction } from 'mobx'
import {
  type Observable,
  defer as observableDefer,
  from,
  mergeMap,
  filter,
} from 'rxjs'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { toStream } from '@/utils/mobx-util'
import { shareLatest } from '@/utils/rx-util'

/**
 * Feature loader function that also exposes reactive properties that indicate
 * whether the module has been requested/loaded. These are useful to
 * conditionally render components that themselves use feature internals but
 * that you don't wish to load until something else triggers it.
 */
export type InternalFeatureLoader<T> = (() => PromiseLike<T>) & {
  /**
   * A reactive value indicating whether the module has been requested to load.
   * Will also return {@value true} if already loaded.
   */
  isRequested(): boolean

  /**
   * A reactive value indicating whether the module has finished loading.
   */
  isLoaded(): boolean

  /**
   * Creates a Promise-like that resolves to the selected value once chained.
   *
   * @param selector
   */
  lazy<U>(selector: (module: T) => U): PromiseLike<NoInfer<U>>

  /**
   * Wraps the selected function so it loads the module
   * when invoked. The returned function will be made async if it isn't already.
   *
   * @param selector
   */
  wrap<A extends unknown[], R>(
    selector: (module: T) => (...args: A) => R,
  ): (...args: A) => Promise<R extends Promise<infer AR> ? AR : R>

  /**
   * Returns an {@link Observable} that, when subscribed to, will load the
   * module and subscribe to the selected observable.
   *
   * @param selector
   */
  defer<R>(selector: (module: T) => Observable<R>): Observable<R>

  /**
   * Returns an {@link Observable} that subscribes to the
   * selected observable only once the module has been loaded.
   *
   * @param selector
   */
  deferUntilLoaded<R>(selector: (module: T) => Observable<R>): Observable<R>
}

/**
 * Creates an internal feature loader.
 *
 * @param moduleLoader Function that imports the module.
 * @param initializer Function that creates an instance of the internal feature.
 */
export function createInternalFeatureLoader<M, T>(
  moduleLoader: () => Promise<M>,
  initializer: (module: M) => T | PromiseLike<T>,
): InternalFeatureLoader<T> {
  const requested = observable.box(false)
  const loaded = observable.box(false)
  let loadPromise: PromiseLike<T> | null = null
  const loaded$ = toStream(() => loaded.get()).pipe(
    filter(Boolean),
    shareLatest(),
  )

  const loadCore = async function () {
    // Since `load` may be called while rendering a component,
    // we can't update the state synchronously, as that results in
    // an error. We'll queue the update in a microtask so it gets
    // done ASAP after any ongoing render is done.
    queueMicrotask(() => runInAction(() => requested.set(true)))
    const module = await moduleLoader()
    const feature = await initializer(module)
    // No scheduling needed here since the `await` above
    // results in this part being run after render.
    runInAction(() => loaded.set(true))
    return feature
  }

  const load = () => {
    if (loadPromise) {
      return loadPromise
    }
    loadPromise = ValueRetrievablePromise.from(loadCore())
    return loadPromise
  }

  const lazy = <U>(selector: (module: T) => U) =>
    ValueRetrievablePromise.from({
      then: <TResult1 = U, TResult2 = never>(
        onfulfilled?: ((value: U) => TResult1 | PromiseLike<TResult1>) | null,
        onRejected?:
          | ((reason: unknown) => TResult2 | PromiseLike<TResult2>)
          | null,
      ): PromiseLike<TResult1 | TResult2> => {
        return load().then((module) => {
          const selected = selector(module)
          return onfulfilled ? onfulfilled(selected) : selected
        }, onRejected) as PromiseLike<TResult1 | TResult2>
      },
    })

  const wrap: InternalFeatureLoader<T>['wrap'] =
    (selector) =>
    // @ts-ignore
    async (...args) => {
      const module = await load()
      const selected = selector(module)
      return selected(...args)
    }

  const defer: InternalFeatureLoader<T>['defer'] = (selector) =>
    observableDefer(() => from(load()).pipe(mergeMap(selector)))

  const deferUntilLoaded: InternalFeatureLoader<T>['deferUntilLoaded'] = (
    selector,
  ) => observableDefer(() => loaded$.pipe(mergeMap(load), mergeMap(selector)))

  return Object.assign(load, {
    lazy,
    wrap,
    defer,
    deferUntilLoaded,
    isRequested: () => requested.get(),
    isLoaded: () => loaded.get(),
  })
}
